import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { motion } from 'framer-motion';
import { Map, List, AlertCircle, Loader, X } from 'lucide-react';
import { 
  getActiveAssignments, 
  getUserPlotAssignment,
  selectPlotForUser,
  PlotAssignment 
} from '../services/housingService';
import HousingMapVisualization from '../components/HousingMapVisualization';

// Parse plot assignment to extract plot number
// Format: "Area - Plot X" -> extract number (normalize to remove leading zeros)
const parsePlotNumber = (assignedPlot: string): string | null => {
  // More flexible regex: handles "Plot 03", "Plot03", "Plot  03" (with multiple spaces)
  const match = assignedPlot.match(/Plot\s*(\d+)/i);
  if (!match) {
    console.warn(`Could not parse plot number from: "${assignedPlot}"`);
    return null;
  }
  // Remove leading zeros to match plot positions format (e.g., "05" -> "5", "03" -> "3")
  const plotNum = parseInt(match[1], 10).toString();
  return plotNum;
};

// Create a map of plot number -> assignment
const createPlotAssignmentMap = (assignments: PlotAssignment[]): Record<string, PlotAssignment> => {
  const plotMap: Record<string, PlotAssignment> = {};
  assignments.forEach(assignment => {
    const plotNum = parsePlotNumber(assignment.assignedPlot);
    if (plotNum) {
      plotMap[plotNum] = assignment;
      console.log(`Mapped assignment: ${assignment.username} -> Plot ${plotNum} (from "${assignment.assignedPlot}")`);
    } else {
      console.warn(`Failed to parse plot number for assignment: ${assignment.username} - "${assignment.assignedPlot}"`);
    }
  });
  console.log(`Created plot assignment map with ${Object.keys(plotMap).length} entries:`, Object.keys(plotMap).sort((a, b) => parseInt(a) - parseInt(b)));
  return plotMap;
};

const HOUSING_DATA = {
  'Cliffside': ['31', '23', '32', '03', '22'],
  'Brumewood Hallow': ['0', '17', '06', '21', '38', '08', '33', '20', '18', '41', '10', '02', '15'],
  'Gilded Oaks': ['13', '30', '01', '35', '26', '37', '05', '14', '36'],
  'The Outer Banks': ['24', '43', '28', '29', '52', '19', '50', '40', '25', '12', '04'],
  'Mear\'s Farm': ['42', '39', '45', '34'],
  'Central': ['48', '53', '44', '27', '07', '09', '47', '51', '49', '46', '54', '16', '11'],
};

// Get area for a plot number
const getPlotArea = (plotNumber: string): string | null => {
  const normalizedPlot = parseInt(plotNumber, 10).toString();
  for (const [area, plots] of Object.entries(HOUSING_DATA)) {
    const normalizedPlots = plots.map(p => parseInt(p, 10).toString());
    if (normalizedPlots.includes(normalizedPlot)) {
      return area;
    }
  }
  return null;
};

// Get all plot numbers from all areas (sorted numerically, normalized to remove leading zeros)
const getAllPlotNumbers = (): string[] => {
  const allPlots: string[] = [];
  Object.values(HOUSING_DATA).forEach(plots => {
    allPlots.push(...plots);
  });
  
  // Normalize plot numbers (remove leading zeros) to match plotAssignmentMap format
  // Sort numerically
  return allPlots
    .map(plotNum => parseInt(plotNum, 10).toString()) // Normalize: "03" -> "3", "05" -> "5"
    .sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      return numA - numB;
    });
};

const HousingMap: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [assignments, setAssignments] = useState<PlotAssignment[]>([]);
  const [plotAssignmentMap, setPlotAssignmentMap] = useState<Record<string, PlotAssignment>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [hoveredPlot, setHoveredPlot] = useState<string | null>(null);
  const [userAssignment, setUserAssignment] = useState<PlotAssignment | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    plotNumber: string | null;
  }>({ show: false, plotNumber: null });
  
  const loadUserAssignment = useCallback(async () => {
    if (!user) return;
    try {
      const assignment = await getUserPlotAssignment(user.uid);
      setUserAssignment(assignment);
    } catch (err: any) {
      console.error('Error loading user assignment:', err);
    }
  }, [user?.uid]);
  
  const loadAssignments = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const activeAssignments = await getActiveAssignments();
      setAssignments(activeAssignments);
      
      // Create map of plot numbers to assignments
      const plotMap = createPlotAssignmentMap(activeAssignments);
      setPlotAssignmentMap(plotMap);
      
      // Reload user assignment after assignments update
      if (user) {
        const assignment = await getUserPlotAssignment(user.uid);
        setUserAssignment(assignment);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);
  
  // Load assignments when user is authenticated
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    
    loadAssignments();
    loadUserAssignment();
  }, [authLoading, user?.uid, loadAssignments, loadUserAssignment, navigate]); // Only depend on user.uid, not the whole user object

  const handlePlotClick = (plotNumber: string) => {
    // Check if plot is already assigned
    const plotAssignment = plotAssignmentMap[plotNumber];
    if (plotAssignment) {
      // If it's the user's own plot, show info (don't allow selecting their own plot again)
      if (plotAssignment.userId === user?.uid) {
        setError(`You already have this plot assigned: ${plotAssignment.assignedPlot}`);
        return;
      }
      // If it's assigned to someone else, show error
      setError(`Plot ${plotNumber} is already assigned to ${plotAssignment.username}`);
      return;
    }

    // Plot is available - check if user already has an assignment
    if (userAssignment) {
      // Show confirmation dialog warning that selecting a new plot will replace their current assignment
      setConfirmDialog({ show: true, plotNumber });
    } else {
      // User has no assignment - directly select the plot
      selectPlot(plotNumber);
    }
  };

  const selectPlot = async (plotNumber: string) => {
    if (!user) return;

    setError(null);
    setSuccess(null);
    setConfirmDialog({ show: false, plotNumber: null });
    setLoading(true);

    try {
      const plotArea = getPlotArea(plotNumber);
      if (!plotArea) {
        setError(`Could not determine area for plot ${plotNumber}`);
        setLoading(false);
        return;
      }

      const result = await selectPlotForUser(
        user.uid,
        user.username || user.email,
        user.username,
        plotNumber,
        plotArea
      );

      if (result) {
        setError(result);
      } else {
        setSuccess(`Successfully selected Plot ${plotNumber} in ${plotArea}`);
        await loadAssignments();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to select plot');
    } finally {
      setLoading(false);
    }
  };
  
  const allPlotNumbers = getAllPlotNumbers();
  
  if (authLoading || loading) {
    return (
      <div className="page-container bg-surface flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader className="text-primary animate-spin" size={48} />
          <p className="text-text-secondary">Loading housing map...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="page-container bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Housing Map</span>
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Please check your assigned plots and let any of the officers know if there are any conflicts or changes requested and we will do our best to accommodate! The map shows all remaining available plots for anyone who did not submit preferences, please feel free to select any of the plots remaining. This will operate on a "first come first serve" basis.
          </p>
          <p className="text-lg text-text-secondary mt-4 font-semibold">
            Thank you for participating!
          </p>
        </motion.div>
        
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3"
          >
            <AlertCircle className="text-red-400" size={20} />
            <p className="text-red-400">{error}</p>
          </motion.div>
        )}

        {/* Success Message */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-6 flex items-center gap-3"
          >
            <AlertCircle className="text-green-400" size={20} />
            <p className="text-green-400">{success}</p>
          </motion.div>
        )}

        {/* Confirmation Dialog */}
        {confirmDialog.show && confirmDialog.plotNumber && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setConfirmDialog({ show: false, plotNumber: null })}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-surface border-2 border-border rounded-lg p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-text-primary">Confirm Plot Selection</h3>
                <button
                  onClick={() => setConfirmDialog({ show: false, plotNumber: null })}
                  className="text-text-secondary hover:text-text-primary transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 mb-4">
                  <p className="text-yellow-400 text-sm font-semibold mb-2">⚠️ Warning</p>
                  <p className="text-text-primary text-sm">
                    You already have an assigned plot: <strong>{userAssignment?.assignedPlot}</strong>
                  </p>
                  <p className="text-text-secondary text-sm mt-2">
                    Selecting Plot {confirmDialog.plotNumber} will replace your current assignment, making it available for others.
                  </p>
                </div>
                <p className="text-text-primary">
                  Are you sure you want to select <strong>Plot {confirmDialog.plotNumber}</strong>?
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmDialog({ show: false, plotNumber: null })}
                  className="px-4 py-2 bg-surface-light text-text-secondary rounded-lg hover:bg-surface-lighter transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => selectPlot(confirmDialog.plotNumber!)}
                  className="px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  Confirm Selection
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* View Mode Toggle */}
        <div className="flex gap-4 mb-6 justify-center">
          <button
            onClick={() => setViewMode('map')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              viewMode === 'map'
                ? 'bg-gradient-to-r from-primary to-secondary text-white'
                : 'bg-surface-light text-text-secondary hover:text-text-primary'
            }`}
          >
            <Map size={20} />
            Map View
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              viewMode === 'list'
                ? 'bg-gradient-to-r from-primary to-secondary text-white'
                : 'bg-surface-light text-text-secondary hover:text-text-primary'
            }`}
          >
            <List size={20} />
            List View
          </button>
        </div>
        
        {/* Map View */}
        {viewMode === 'map' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass p-8 rounded-xl"
          >
            <HousingMapVisualization
              assignments={assignments}
              plotAssignmentMap={plotAssignmentMap}
              onPlotClick={handlePlotClick}
              userAssignment={userAssignment}
            />
            {assignments.length === 0 && (
              <div className="mt-4 bg-blue-500/20 border border-blue-500/50 rounded-lg p-4">
                <p className="text-blue-400 text-sm">
                  <strong>Note:</strong> All plots are currently available. Run the assignment algorithm to see plot assignments on the map.
                </p>
              </div>
            )}
          </motion.div>
        )}
        
        {/* List View */}
        {viewMode === 'list' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass p-8 rounded-xl"
          >
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <List className="text-primary" size={24} />
              Plot Assignments List
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allPlotNumbers.map(plotNum => {
                const assignment = plotAssignmentMap[plotNum];
                return (
                  <div
                    key={plotNum}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      assignment
                        ? assignment.userId === user?.uid
                          ? 'bg-blue-500/20 border-blue-500/50'
                          : 'bg-green-500/20 border-green-500/50'
                        : 'bg-surface-light border-border hover:border-primary cursor-pointer'
                    }`}
                    onMouseEnter={() => setHoveredPlot(plotNum)}
                    onMouseLeave={() => setHoveredPlot(null)}
                    onClick={() => !assignment && handlePlotClick(plotNum)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-lg text-text-primary">
                        Plot {plotNum}
                      </span>
                      {assignment ? (
                        <span className={`px-2 py-1 text-xs rounded ${
                          assignment.userId === user?.uid
                            ? 'bg-blue-500/30 text-blue-400'
                            : 'bg-green-500/30 text-green-400'
                        }`}>
                          {assignment.userId === user?.uid ? 'Your Plot' : 'Assigned'}
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-primary/30 text-primary text-xs rounded cursor-pointer">
                          Click to Select
                        </span>
                      )}
                    </div>
                    {assignment ? (
                      <div>
                        <p className="text-text-primary font-semibold">
                          {assignment.username}
                        </p>
                        {assignment.characterName && (
                          <p className="text-text-secondary text-sm">
                            {assignment.characterName}
                          </p>
                        )}
                        <p className="text-text-secondary text-xs mt-1">
                          {assignment.assignedPlot}
                        </p>
                      </div>
                    ) : (
                      <p className="text-text-secondary text-sm italic">
                        Unassigned
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Summary Stats */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-surface-light rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-text-primary">
                  {allPlotNumbers.length}
                </p>
                <p className="text-sm text-text-secondary">Total Plots</p>
              </div>
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-400">
                  {assignments.length}
                </p>
                <p className="text-sm text-text-secondary">Assigned</p>
              </div>
              <div className="bg-surface-light rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-text-primary">
                  {allPlotNumbers.length - assignments.length}
                </p>
                <p className="text-sm text-text-secondary">Available</p>
              </div>
              <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">
                  {assignments.filter(a => a.satisfiedNeighborRequest).length}
                </p>
                <p className="text-sm text-text-secondary">Neighbor Matches</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default HousingMap;

